import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiGet, apiPatch, operationsClient } from '@/lib/api/client';
import { settingsEndpoints } from '@/lib/api/endpoints';
import { settingsKeys } from '@/lib/queryKeys';
import type { AccountingSettings, UpdateAccountingSettingsDto } from '@/types/settings';

/**
 * Fetch the tenant's accounting configuration (deposit mode, GL account
 * mapping, payment method mapping, plus per-entity sync flags for
 * expenses/memberships/deposits/change-orders).
 *
 * Spec §16.2 — tenant-level accounting settings.
 */
export function useAccountingSettings() {
  return useApiQuery<AccountingSettings>({
    queryKey: settingsKeys.qbAccountConfig(),
    queryFn: () =>
      apiGet<AccountingSettings>(operationsClient, settingsEndpoints.qbAccountConfig()),
    showErrorToast: false,
  });
}

/**
 * Patch the tenant's accounting configuration. Only fields present in the
 * DTO are updated server-side. JSON-typed maps merge with the existing
 * object so partial updates don't wipe entries.
 */
export function useUpdateAccountingSettings() {
  const queryClient = useQueryClient();
  return useApiMutation<AccountingSettings, UpdateAccountingSettingsDto>({
    mutationFn: (dto) =>
      apiPatch<AccountingSettings, UpdateAccountingSettingsDto>(
        operationsClient,
        settingsEndpoints.qbAccountConfig(),
        dto,
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(settingsKeys.qbAccountConfig(), data);
      toast.success('Accounting settings saved');
    },
  });
}
